import Phaser from 'phaser';
import { getPlayerBonuses, setPlayerBonuses, type PlayerBonuses } from '../game/playerBonuses';

export type StatOffer = {
  id: string;
  title: string;
  desc: string;
  apply: (b: PlayerBonuses) => PlayerBonuses;
};

const OFFER_POOL: StatOffer[] = [
  {
    id: 'vit_a',
    title: '견고함',
    desc: '최대 체력 +2',
    apply: (b) => ({ ...b, maxHpFlat: b.maxHpFlat + 2 }),
  },
  {
    id: 'vit_b',
    title: '불굴',
    desc: '최대 체력 +3',
    apply: (b) => ({ ...b, maxHpFlat: b.maxHpFlat + 3 }),
  },
  {
    id: 'agi_a',
    title: '기민함',
    desc: '이동 속도 +10%',
    apply: (b) => ({ ...b, speedMult: b.speedMult * 1.1 }),
  },
  {
    id: 'agi_b',
    title: '질주',
    desc: '이동 속도 +14%',
    apply: (b) => ({ ...b, speedMult: b.speedMult * 1.14 }),
  },
  {
    id: 'atk_a',
    title: '연발',
    desc: '발사 간격 -10%',
    apply: (b) => ({ ...b, fireRateMult: b.fireRateMult * 0.9 }),
  },
  {
    id: 'atk_b',
    title: '침착',
    desc: '발사 간격 -14%',
    apply: (b) => ({ ...b, fireRateMult: b.fireRateMult * 0.86 }),
  },
];

/** `reward`: 구역 클리어 직후 거점으로 돌아가기 전 보상 / `preCombat`은 레거시 진입 전 선택. */
export class StatPickScene extends Phaser.Scene {
  private stageId = '1-1';
  private mode: 'reward' | 'preCombat' = 'reward';

  constructor() {
    super({ key: 'StatPickScene' });
  }

  init(data: { stageId?: string; mode?: 'reward' | 'preCombat' }): void {
    this.stageId = data?.stageId ?? '1-1';
    this.mode = data?.mode ?? 'reward';
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#141018');

    const title =
      this.mode === 'reward'
        ? `지옥 I · ${this.stageId} 나선 통과`
        : `포털 · 지옥 I · ${this.stageId}`;

    this.add
      .text(width / 2, height * 0.2, title, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '26px',
        color: '#f4f0fb',
      })
      .setOrigin(0.5);

    const subtitle =
      this.mode === 'reward'
        ? '나선이 빚어 낸 보상 — 아래 셋 중 하나를 골라 다음 원정에 이어지게 하십시오.'
        : '문 너머로 건너가기 전, 나선이 제안하는 힘을 하나 고르십시오.';

    this.add
      .text(width / 2, height * 0.3, subtitle, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#a39cb8',
        align: 'center',
        wordWrap: { width: width - 56 },
      })
      .setOrigin(0.5);

    const offers = Phaser.Utils.Array.Shuffle([...OFFER_POOL]).slice(0, 3);
    const xs = [width * 0.2, width * 0.5, width * 0.8];
    const y = height * 0.56;

    offers.forEach((offer, i) => {
      const x = xs[i] ?? width * 0.5;
      const btn = this.add
        .text(x, y, `${offer.title}\n${offer.desc}`, {
          fontFamily: 'system-ui, "Segoe UI", sans-serif',
          fontSize: '14px',
          color: '#eeeaf8',
          backgroundColor: '#3d3350dd',
          align: 'center',
          lineSpacing: 6,
          padding: { left: 14, right: 14, top: 14, bottom: 14 },
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(10);

      btn.on('pointerover', () => btn.setAlpha(1));
      btn.on('pointerout', () => btn.setAlpha(0.95));
      btn.on('pointerdown', () => this.pick(offer));
      btn.setAlpha(0.95);
    });

    this.add
      .text(
        width / 2,
        height - 36,
        this.mode === 'reward' ? '선택 후 거점으로 돌아갑니다.' : '선택 후 해당 구역 전투가 시작됩니다.',
        {
          fontFamily: 'system-ui, "Segoe UI", sans-serif',
          fontSize: '12px',
          color: '#7d7794',
        },
      )
      .setOrigin(0.5);
  }

  private pick(offer: StatOffer): void {
    const cur = getPlayerBonuses(this.registry);
    setPlayerBonuses(this.registry, offer.apply(cur));
    if (this.mode === 'reward') {
      this.scene.start('HubScene');
    } else {
      this.scene.start('Hell1CombatScene', { stageId: this.stageId });
    }
  }
}
